import type { ReactNode } from 'react';
import { Box, Flex, Alert, AlertIcon, Link, Icon } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ExternalLink, Landmark } from 'lucide-react';

import { isDefined } from '@duke-hq/lib';

import type { ChatActions } from '../Context';
import { Chat } from '../Chat';
import { ChatProvider } from '../ChatProvider';
import { TicketHeader } from './TicketHeader';
import { EmptyTicketSelection } from './EmptyTicketSelection';
import { iconSize } from '@duke-hq/ui/constants/scalable';

type Interaction = {
  id: string;
  interactionKind: string;
  interactionId: string;
};

type Ticket = {
  requesterName: string;
  requesterType: string;
  requesterEmail: string;
  isProviderTicket?: boolean;
  interactions: Interaction[];
  description: string | null;
  studentId?: string | null;
  contextStudentId?: string;
};

type TicketDetailsAreaProps = {
  ticket?: Ticket;
  currentUserDrn?: string;
  chatActions: ChatActions;
  headerActions?: ReactNode;
  statusBar?: ReactNode;
  // Rendered in place of the chat input when the reader cannot write here.
  composerNotice?: ReactNode;
};

export function TicketDetailsArea({
  ticket,
  currentUserDrn,
  chatActions,
  headerActions,
  statusBar,
  composerNotice,
}: TicketDetailsAreaProps) {
  if (ticket === undefined) {
    return <EmptyTicketSelection />;
  }

  return (
    <Flex direction="column" flex={1} overflow="hidden" bg="bg.surface">
      <TicketHeader
        requesterName={ticket.requesterName}
        requesterType={ticket.requesterType}
        requesterEmail={ticket.requesterEmail}
        requesterTypeIcon={
          ticket.isProviderTicket === true ? (
            <Landmark size={iconSize.sm} strokeWidth={2} />
          ) : undefined
        }
        requesterLink={
          isDefined(ticket.studentId) ? `/student/${ticket.studentId}/enrolments` : undefined
        }
        actions={headerActions}
      />

      {statusBar}

      {ticket.description !== null && ticket.description !== '' ? (
        <Alert status="info" variant="subtle" borderRadius="0">
          <AlertIcon />
          {ticket.description}
          {ticket.contextStudentId !== undefined ? (
            <Link
              aria-label="View student enrolments"
              as={RouterLink}
              to={`/student/${ticket.contextStudentId}/enrolments`}
              target="_blank"
              rel="noopener noreferrer"
              color="brand.500"
              ml="2"
              display="inline-flex"
              alignItems="center"
              gap="1"
              _hover={{ textDecoration: 'underline', color: 'brand.700' }}
            >
              <Icon as={ExternalLink} boxSize="3" />
            </Link>
          ) : null}
        </Alert>
      ) : null}

      {currentUserDrn !== undefined ? (
        <Box flex={1} overflow="auto">
          {ticket.interactions.map((interaction) => {
            if (interaction.interactionKind === 'CHAT') {
              return (
                <ChatProvider key={interaction.id} chatActions={chatActions}>
                  <Chat
                    chatId={interaction.interactionId}
                    currentUserDrn={currentUserDrn}
                    composerNotice={composerNotice}
                  />
                </ChatProvider>
              );
            }

            return null;
          })}
        </Box>
      ) : null}
    </Flex>
  );
}
